import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { userShape, bookingShape } from 'constants/propTypes';
import { useMutation } from '@apollo/client';
import productTypes from 'constants/productTypes';
import { isUserEurostarAgent, isUserStationAgent } from 'utils/booking';
import { sum } from 'utils/calculator';
import { Row } from 'components/shared/Layout';
import PricesAndPointsSummary from 'components/shared/PricesAndPointsSummary';
import { isGroupBooking } from 'utils/isGroupBooking';
import { ANY_BELGIAN_STATION_SHORT_TARIFF_CODE, ADS_THROUGH_BRUSSELS_SHORT_TARIFF_CODE } from 'constants/booking';
import { DELETE_BOOKING_ITEMS, DELETE_ADDITIONAL_DETAILS } from 'store/mutations';
import { ActionButton, CancelButton, ConfirmButton } from 'components/ui/Button';
import { useHistory } from 'react-router';
import config from 'utils/config';
import OverrideFeesCta from 'components/shared/OverrideFeesCta';
import formatReason from 'utils/formatReason';
import { isTravelAgent } from 'utils/user';
import { WAIVE_OFFLINE_FEES } from 'store/mutations';
import usePayIfLoyaltyAndWaiveOfflineFeesMutation from 'components/mutations/usePayIfLoyaltyAndWaiveOfflineFeesMutation';
import { getBookingAttachedLoyaltyAccount } from 'utils/booking';
import SectionHeaderBar from './SectionHeaderBar';
import SegmentSummary from './SegmentSummary';
import InfoWithPrice from './SegmentSummary/SegmentSummaryColumn/SegmentInfo/InfoWithPrice';
import ExtrasHotel from './HotelsReviewBooking';
import DiscountCodeForm from './DiscountCodeForm';
import Modal from 'components/ui/Modal';
import ExtrasSubscriptions from 'pages/BookingDetails/Extras/ExtrasSubscriptions';
import AlertBox from 'components/ui/AlertBox';

import {
  StyleHeaderItems,
  StyledBody,
  StyleHeaderItem,
  StyledInfoWithPriceWrapper,
  StyledAssistancebox
} from './BookingPathReviewBookingSummary.styles';

import { getInfantPassengersFromDetails } from 'utils/getInfantPassengersFromDetails';
import { INFANT_FIRST_NAME_KEY, INFANT_LAST_NAME_KEY } from 'constants/passengers';
import {
  getOfflineFees,
  getTotalPrice,
  getTotalPoints,
  getHotelPrices,
  getHotelMargins,
  getMultiJourneyLegs,
  hasHotels,
  isBookingMultiJourney,
  getHotels,
  hasOnlyERLegs,
  trainMarginCodes,
  isBookingMiceGroup
} from './BookingPathReviewBookingSummary.utils';

const BookingPathReviewBookingSummary = ({
  user,
  title,
  currency,
  booking = {},
  refetch,
  children,
  displayContactEBSCAssistance = false
}) => {
  const [deleteAdditionalDetails, { loading: deleteAdditionalDetailsLoading }] = useMutation(DELETE_ADDITIONAL_DETAILS);
  const [deleteBookingItems, { loading: deleteBookingItemsLoading }] = useMutation(DELETE_BOOKING_ITEMS);
  const [waiveOfflineFees, { loading: waiveOfflineFeesLoading }] = useMutation(WAIVE_OFFLINE_FEES);
  const [payIfLoyaltyAndWaiveOfflineFees, { loading: payIfLoyaltyAndWaiveOfflineFeesLoading }] =
    usePayIfLoyaltyAndWaiveOfflineFeesMutation();
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [journeyIdToRemove, setJourneyIdToRemove] = useState(null);
  const history = useHistory();
  const isThalysMultiJourneyEnabled = config('ENABLE_THALYS_MULTI_JOURNEY', { canOverride: true }) === 'true';
  const isSubscriptionFlow = new URLSearchParams(history.location.search).get('isSubscription');
  const isMultiJourney = isBookingMultiJourney(booking);
  const isMiceGroupBooking = isBookingMiceGroup({ details: booking.additionalDetails });

  let multiJourneyLegs = [];

  if (isMultiJourney) {
    multiJourneyLegs = getMultiJourneyLegs(booking);
  }

  const { currency: bookingCurrency, reference, productFamilies, totalPriceToBePaid, isLoyalty, fees, discounts = [] } = booking;

  const infantPassengers = getInfantPassengersFromDetails({ additionalDetails: booking.additionalDetails }).map((infant) => ({
    firstName: infant[INFANT_FIRST_NAME_KEY] || '',
    lastName: infant[INFANT_LAST_NAME_KEY] || '',
    passengerRef: infant.passengerRef
  }));

  const offlineFees = getOfflineFees(fees);
  const firstOfflineFee = offlineFees?.length > 0 ? offlineFees[0] : null;
  const hasOfflineFees = Boolean(firstOfflineFee) && firstOfflineFee.price > 0;
  const canOverrideFees = Boolean(user?.canOverrideFees) && hasOfflineFees && !isTravelAgent(user);

  const hasNonCancelledHostels = hasHotels(booking.hotels);
  const hasHotelsPrice = Boolean(hasNonCancelledHostels) && getHotels(booking.hotels)[0].price >= 0;
  const hotelsPrice = hasHotelsPrice && getHotelPrices(booking.hotels);
  const hotelsMargin = hasHotelsPrice && getHotelMargins(booking.hotels);

  const totalPoints = getTotalPoints(booking);
  let totalPrice = getTotalPrice(booking);
  const bookingLegs = booking?.outbound?.concat(booking?.inbound);

  // Get membership number from URL or booking additionalDetails
  const membershipNumber =
    new URLSearchParams(history.location.search).get('membershipNumber') || getBookingAttachedLoyaltyAccount(booking);
  const chosenCurrency = currency || bookingCurrency;

  const canApplyDiscountCode = isUserEurostarAgent(user) && !hasNonCancelledHostels;
  const discountAmount = discounts.length > 0 ? sum(discounts.map((discount) => discount.value)) : 0;
  const originalFare = isSubscriptionFlow ? sum([totalPriceToBePaid, discountAmount]) : sum([totalPrice, discountAmount]);
  const productFamilyCode = productFamilies && productFamilies[0].familyGroup;
  const groupName = isGroupBooking(productFamilyCode) ? booking?.customer?.companyName || '' : '';

  const shouldEnableMultiJourney =
    isUserEurostarAgent(user) && !isUserStationAgent(user) && hasOnlyERLegs(bookingLegs) && isThalysMultiJourneyEnabled;

  const trainsMargin = bookingLegs
    .map((leg) => leg.extraProducts)
    .flat()
    .filter(
      (extra) =>
        extra.type !== productTypes.DISABILITY_SUPPORT &&
        trainMarginCodes.includes(extra.code) &&
        extra?.tariffDetails?.tariffCode?.slice(0, 3) !== ANY_BELGIAN_STATION_SHORT_TARIFF_CODE &&
        extra?.tariffDetails?.tariffCode?.slice(0, 3) !== ADS_THROUGH_BRUSSELS_SHORT_TARIFF_CODE
    );

  const trainsMarginTotal = trainsMargin.reduce((acc, e) => acc + e.price, 0);

  const extras = bookingLegs
    .map((bl) => bl.extraProducts)
    .flat()
    .filter((ep) => ep.type !== productTypes.DISABILITY_SUPPORT)
    .filter((extra) => !trainMarginCodes.includes(extra.code))
    ?.filter(
      (epp) =>
        epp?.tariffDetails?.tariffCode?.slice(0, 3) !== ANY_BELGIAN_STATION_SHORT_TARIFF_CODE &&
        epp?.tariffDetails?.tariffCode?.slice(0, 3) !== ADS_THROUGH_BRUSSELS_SHORT_TARIFF_CODE
    );

  const extrasTotal = extras.reduce((acc, e) => acc + e.price, 0);

  if (extras.length > 0) {
    totalPrice -= extrasTotal;
  }

  const handleWaiveOfflineFees = async (formState) => {
    try {
      const reason = formatReason(formState.reasonCode, formState.additionalExplanation, formState.label);

      if (isLoyalty && membershipNumber) {
        await payIfLoyaltyAndWaiveOfflineFees({
          variables: {
            membershipNumber,
            bookingData: booking,
            actionBy: user.username,
            reason
          }
        });
      } else {
        await waiveOfflineFees({
          variables: {
            bookingReference: reference,
            reason
          }
        });
      }

      refetch({ reference: booking.reference });
    } catch (error) {
      console.error('Failed to waive offline fees:', error);
    }
  };

  const onRemoveJourney = (journeyId) => {
    setJourneyIdToRemove(journeyId);
    setIsRemoveModalOpen(!isRemoveModalOpen);
  };

  const onRemoveModalClose = () => {
    setIsRemoveModalOpen(!isRemoveModalOpen);
    setJourneyIdToRemove(null);
  };

  const onRemoveJourneyConfirm = async () => {
    let segmentIds = booking.additionalDetails.filter((ad) => ad.value === `journey_${journeyIdToRemove}`).map((ad) => ad.key);

    let itemRefs = booking.outbound
      .concat(booking.inbound)
      .filter((l) => segmentIds.includes(l.id))
      .map((l) => l.products.filter((p) => !p.cancelled))
      .flat()
      .map((p) => Number(p.itemRef));

    await deleteBookingItems({
      variables: {
        bookingReference: booking.reference,
        itemRefs
      }
    });

    const additionalDetails = segmentIds.map((s) => ({ key: s }));

    // If only 2 journeys and we're deleting 1, then remove multi_journey flag and remove all journey mappings
    if (multiJourneyLegs.length === 2) {
      const otherJourneyId = multiJourneyLegs.find((l) => l.journeyId !== journeyIdToRemove)?.journeyId;

      additionalDetails.push(
        ...booking.additionalDetails.filter((ad) => ad.value === `journey_${otherJourneyId}`).map((ad) => ({ key: ad.key }))
      );

      additionalDetails.push({ key: 'multi_journey' });
    }

    await deleteAdditionalDetails({
      variables: {
        bookingReference: booking.reference,
        additionalDetails
      }
    });

    refetch({ reference: booking.reference });

    onRemoveModalClose();
  };

  return (
    <>
      <Modal
        isOpen={isRemoveModalOpen}
        onClose={onRemoveModalClose}
        title={<FormattedMessage id="review.removeModal.title" />}
        subtitle={<FormattedMessage id="review.removeModal.subtitle" />}
      >
        <ConfirmButton
          data-cy="exchanges-continue"
          onClick={onRemoveJourneyConfirm}
          disabled={deleteBookingItemsLoading || deleteAdditionalDetailsLoading}
          loading={deleteBookingItemsLoading || deleteAdditionalDetailsLoading}
          variant="primary"
        >
          <FormattedMessage id="review.removeModal.confirm" />
        </ConfirmButton>
        <CancelButton onClick={onRemoveModalClose}>
          <FormattedMessage id="review.removeModal.abandon" />
        </CancelButton>
      </Modal>

      <SectionHeaderBar>
        <FormattedMessage id={title} />
        <>
          {reference && (
            <StyleHeaderItems data-id="booking-details" className="booking-pnr">
              <FormattedMessage id="searchBooking.pnr" />
              <StyleHeaderItem data-cy="pnr">{reference}</StyleHeaderItem>
            </StyleHeaderItems>
          )}

          <StyleHeaderItems className="booking-total">
            <FormattedMessage id="summary.total" />
            <StyleHeaderItem data-cy="summary-price">
              <PricesAndPointsSummary price={totalPriceToBePaid} currency={chosenCurrency} isLoyalty={isLoyalty} points={totalPoints} />
            </StyleHeaderItem>
          </StyleHeaderItems>
        </>
      </SectionHeaderBar>

      <StyledBody data-cy="review-booking-segment">
        {displayContactEBSCAssistance && (
          <StyledAssistancebox>
            <AlertBox data-testid="reviewBooking.contactEBSCWarning" variant={AlertBox.variants.WARNING}>
              <FormattedMessage id="reviewBooking.contactEBSCWarning" />
            </AlertBox>
          </StyledAssistancebox>
        )}
        {!isMultiJourney && isSubscriptionFlow && <ExtrasSubscriptions travelPassProducts={booking.travelPassProducts} />}

        {!isMultiJourney && !isSubscriptionFlow && reference && (
          <>
            <SegmentSummary
              currency={chosenCurrency}
              productFamilies={productFamilies}
              bookingLegs={bookingLegs}
              groupName={groupName}
              isMiceGroupBooking={isMiceGroupBooking}
              infantPassengers={infantPassengers}
              isLoyaltyBooking={isLoyalty}
            />
          </>
        )}

        {isMultiJourney &&
          !isSubscriptionFlow &&
          reference &&
          multiJourneyLegs
            .filter((l) => l.legs.length > 0)
            .map((l, index) => (
              <React.Fragment key={index}>
                <h1>
                  <FormattedMessage id="multiJourney.journeyLabelId" values={{ journeyId: index + 1 }} />
                </h1>

                <SegmentSummary
                  currency={chosenCurrency}
                  productFamilies={productFamilies}
                  bookingLegs={l.legs}
                  groupName={groupName}
                  isMiceGroupBooking={isMiceGroupBooking}
                  infantPassengers={infantPassengers}
                  isLoyaltyBooking={isLoyalty}
                />

                <CancelButton data-cy="remove-journey" type="button" onClick={() => onRemoveJourney(l.journeyId)}>
                  <FormattedMessage id="review.removeJourney" />
                </CancelButton>
              </React.Fragment>
            ))}
        {children}
      </StyledBody>

      {shouldEnableMultiJourney && !isSubscriptionFlow && (
        <>
          <ActionButton
            data-cy="add-journey"
            type="button"
            onClick={() => history.push(`/booking/${reference}/search`, history.location.state)}
          >
            <FormattedMessage id="review.addJourney" />
          </ActionButton>
        </>
      )}

      {hasHotelsPrice && (
        <>
          <SectionHeaderBar>
            <FormattedMessage id="bookingDetails.hotelSummary" />
          </SectionHeaderBar>
          <StyledBody data-cy="cost-of-hotels">
            <ExtrasHotel hotels={booking.hotels} bookingReference={reference} />
          </StyledBody>
        </>
      )}

      {canApplyDiscountCode && (
        <>
          <SectionHeaderBar>
            <FormattedMessage id="bookingDetails.applyDiscountCode" />
          </SectionHeaderBar>
          <StyledBody data-cy="apply-discount-code">
            <DiscountCodeForm bookingReference={reference} appliedDiscounts={discounts} />
          </StyledBody>
        </>
      )}

      <SectionHeaderBar>
        <FormattedMessage id="bookingDetails.costOfBooking" />
      </SectionHeaderBar>
      <StyledBody data-cy="cost-of-booking">
        <StyledInfoWithPriceWrapper>
          {canOverrideFees && (
            <Row>
              <OverrideFeesCta
                showLink={canOverrideFees}
                onSubmit={handleWaiveOfflineFees}
                includeReason={true}
                includeFareDiff={false}
                includeOfflineFee={true}
                includeExchangeFee={false}
                loading={waiveOfflineFeesLoading || payIfLoyaltyAndWaiveOfflineFeesLoading}
              />
            </Row>
          )}
          <Row>
            {discountAmount > 0 && (
              <>
                <InfoWithPrice
                  price={originalFare}
                  pricePoints={0}
                  value={null}
                  labelId="journeySegment.originalFare"
                  dataId="original-fare"
                  currency={currency}
                  dataCy="info-original-fare"
                />
                <InfoWithPrice
                  price={discountAmount}
                  pricePoints={0}
                  value={null}
                  labelId="journeySegment.discount"
                  dataId="discount"
                  currency={currency}
                  dataCy="info-discount"
                />
              </>
            )}
            {!isSubscriptionFlow && (
              <InfoWithPrice
                price={totalPrice}
                pricePoints={totalPoints}
                value={null}
                labelId="journeySegment.fare"
                dataId="total"
                currency={currency}
                dataCy="info-total-price"
              />
            )}
            {hasHotelsPrice && (
              <>
                <InfoWithPrice
                  price={hotelsPrice}
                  pricePoints={0}
                  value={null}
                  labelId="extras.hotel.modal.price"
                  dataId="extras-hotels-total"
                  currency={currency}
                  dataCy="info-extras-hotels-total"
                />
                <InfoWithPrice
                  price={hotelsMargin}
                  pricePoints={0}
                  value={null}
                  labelId="extras.hotel.modal.margin"
                  dataId="extras-hotels-margin"
                  currency={currency}
                  dataCy="info-extras-hotels-margin"
                />
              </>
            )}
            {trainsMargin.length > 0 && trainsMarginTotal > 0 && (
              <InfoWithPrice
                price={trainsMarginTotal}
                pricePoints={0}
                value={null}
                labelId="segmentSummary.train.margin"
                dataId="train-margin-total"
                currency={currency}
                dataCy="train-margin-total"
              />
            )}
            {extras.length > 0 && (
              <InfoWithPrice
                price={extrasTotal}
                pricePoints={0}
                value={null}
                labelId="segmentInfo.extras"
                dataId="total"
                currency={currency}
                dataCy="info-total-extras"
              />
            )}
            {hasOfflineFees && (
              <InfoWithPrice
                price={firstOfflineFee.price}
                pricePoints={0}
                value={null}
                labelId="journeySummary.offlineFee"
                dataId="total"
                currency={currency}
                dataCy="info-total-fees"
              />
            )}
            <InfoWithPrice
              price={totalPriceToBePaid}
              pricePoints={totalPoints}
              value={null}
              labelId="summary.total"
              dataId="total"
              currency={currency}
              dataCy="info-total-price-to-be-paid"
            />
          </Row>
        </StyledInfoWithPriceWrapper>
      </StyledBody>
    </>
  );
};

BookingPathReviewBookingSummary.propTypes = {
  title: PropTypes.string,
  booking: bookingShape,
  refetch: PropTypes.func.isRequired,
  currency: PropTypes.string,
  user: userShape,
  children: PropTypes.node,
  displayContactEBSCAssistance: PropTypes.bool
};

export default BookingPathReviewBookingSummary;
